import {
    type GenericTrackingEvent,
    type LinkInfo,
    type NavigationTrackingEvent,
    type TrackingEvent,
    TrackingEventLocation,
    TrackingEventName,
    TrackingEventType
} from "./types";
import {Nav} from "../common/nav";
import type {Maybe, Nullable} from "../bootstrap/common/types";
import {autoRegister, resolve} from "../container";
import {TrackingContext} from "./trackingContext";
import {omitUndefined} from "../common/utils/objects";
import {DATA_TRACKING_EVENT_NAME, DATA_TRACKING_PAYLOAD_PREFIX} from "./trackingAttributes";

export interface TrackingEventFactory {
    createFromAttributes: (element: Element, event: Event) => TrackingEvent<any>;
}

export class GenericTrackingEventFactory implements TrackingEventFactory {
    public constructor(private nav: Nav) {
    }

    public createFromAttributes(element: Element, event: Event): GenericTrackingEvent {
        const eventName = element.getAttribute(DATA_TRACKING_EVENT_NAME) as TrackingEventName;
        if (!eventName || !Object.values(TrackingEventName).includes(eventName)) {
            throw new Error(`Missing or invalid tracking event name: ${eventName}`);
        }
        const text = this.getTrackingPayloadAttribute(element, "text");
        const type = this.getTrackingPayloadAttribute(element, "type") as TrackingEventType ?? TrackingEventType.CLICK;
        const linkInfo = this.linkInfo(this.getTrackingPayloadAttribute(element, "link"));
        if (!text) {
            throw new Error("Missing required tracking payload attribute: text");
        }
        return {
            name: eventName,
            data: omitUndefined({
                text: text,
                type: type,
                link: linkInfo,
                contentId: TrackingContext.makeContentId(event)
            })
        };
    }

    protected getTrackingPayloadAttribute(element: Element, key: string): Nullable<string> {
        return element.getAttribute(DATA_TRACKING_PAYLOAD_PREFIX + key);
    }

    protected linkInfo(targetUrl: Nullable<string>): Maybe<LinkInfo> {
        if (!targetUrl) {
            return undefined;
        }
        return {
            targetUrl: this.nav.absoluteHrefFrom(targetUrl),
            isExternal: !this.nav.isInternalLink(targetUrl)
        };
    }
}

export class NavigationTrackingEventFactory extends GenericTrackingEventFactory {
    public createFromAttributes(element: Element, event: Event): NavigationTrackingEvent {
        const trackingEvent = super.createFromAttributes(element, event) as NavigationTrackingEvent;
        let location = this.getTrackingPayloadAttribute(element, "location") as TrackingEventLocation;
        if (!location) {
            location = this.locationOf(event);
        } else if (!Object.values(TrackingEventLocation).includes(location)) {
            throw new Error(`Invalid tracking payload attribute for location: ${location}`);
        }
        trackingEvent.name = TrackingEventName.NAVIGATION_INTERACT;
        trackingEvent.data.location = location;
        return trackingEvent;
    }

    private locationOf(event: Event): TrackingEventLocation {
        const eventPath: Set<string> = new Set(Array.from(event.composedPath())
            .filter(element => element instanceof Element)
            .map(element => element.tagName));
        if (eventPath.has("HEADER")) {
            return TrackingEventLocation.HEADER;
        }
        if (eventPath.has("FOOTER")) {
            return TrackingEventLocation.FOOTER;
        }
        if (eventPath.has("MAIN")) {
            return TrackingEventLocation.MAIN;
        }
        return TrackingEventLocation.IN_SITE;
    }
}

@autoRegister()
export class TrackingEventFactories {
    private factories: Map<TrackingEventName, TrackingEventFactory>;

    public constructor(private nav: Nav = resolve(Nav)) {
        const genericTrackingEventFactory = new GenericTrackingEventFactory(this.nav);
        this.factories = new Map([
            [TrackingEventName.TEXTLINK_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.BUTTON_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.TEASER_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.ACCORDION_INTERACT, genericTrackingEventFactory],
            [TrackingEventName.NAVIGATION_INTERACT, new NavigationTrackingEventFactory(this.nav)]
        ]);
    }

    public createFromAttributes(event: Event): Nullable<TrackingEvent<any>> {
        const trackableElement = Array.from(event.composedPath())
            .filter(element => element instanceof Element)
            .filter(element => element.hasAttribute(DATA_TRACKING_EVENT_NAME))
            .first();
        if (!trackableElement) {
            return null;
        }

        const name = trackableElement.getAttribute(DATA_TRACKING_EVENT_NAME) as TrackingEventName;
        const factory = this.factories.get(name);
        if (!factory) {
            throw new Error(`No factory found for tracking event name: ${name}`);
        }
        return factory.createFromAttributes(trackableElement, event);
    }
}